import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { casesAPI, usersAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  Plus, Search, MapPin, Calendar, Hash, FolderOpen,
  RefreshCw, User, X, Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import SearchableSelect from '../components/SearchableSelect';

/* ── Priority config ─────────────────────────────────── */
const PRIORITY = {
  high:   { label: 'High Priority', border: 'border-l-red-500',    badge: 'border-red-400 text-red-500 bg-red-50'       },
  medium: { label: 'Medium',        border: 'border-l-orange-400', badge: 'border-orange-400 text-orange-500 bg-orange-50' },
  low:    { label: 'Low',           border: 'border-l-green-500',  badge: 'border-green-400 text-green-500 bg-green-50'  },
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'adjourned', label: 'Adjourned' },
  { value: 'disposed', label: 'Disposed' },
  { value: 'stayed', label: 'Stayed' },
  { value: 'pending', label: 'Pending' },
  { value: 'closed', label: 'Closed' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'civil', label: 'Civil' },
  { value: 'criminal', label: 'Criminal' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'writ', label: 'Writ Petition' },
  { value: 'appeal', label: 'Appeal' },
  { value: 'arbitration', label: 'Arbitration' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priority' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const PRIORITY_SELECT_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/* ── Helpers ─────────────────────────────────────────── */
function safeFormat(dateStr, fmt) {
  if (!dateStr) return null;
  try { return format(new Date(dateStr), fmt); } catch { return null; }
}

/* ── Preview Field (read-only label+value pair) ──────── */
function PreviewField({ label, value, colSpan }) {
  if (!value && value !== 0) return null;
  return (
    <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-sm text-gray-800 whitespace-pre-wrap">{String(value)}</p>
    </div>
  );
}

/* ── Add Case Modal ──────────────────────────────────── */
function AddCaseModal({ onClose, onSaved }) {
  const navigate = useNavigate();

  // Step: 'input' | 'preview'
  const [step, setStep] = useState('input');

  // Step 1 state
  const [cnr, setCnr] = useState('');
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [existingId, setExistingId] = useState(null);

  // Step 2 state — fetched court data
  const [caseData, setCaseData] = useState(null);

  // Internal fields
  const [lawyers, setLawyers] = useState([]);
  const [assignedLawyer, setAssignedLawyer] = useState('');
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const cnrInputRef = useRef(null);

  // Load lawyers when modal opens
  useEffect(() => {
    usersAPI.lawyers()
      .then(r => setLawyers(r.data || []))
      .catch(() => {});
  }, []);

  // Focus CNR input on open
  useEffect(() => {
    if (step === 'input' && cnrInputRef.current) {
      cnrInputRef.current.focus();
    }
  }, [step]);

  const resetAll = () => {
    setStep('input');
    setCnr('');
    setFetching(false);
    setFetchError(null);
    setExistingId(null);
    setCaseData(null);
    setAssignedLawyer('');
    setPriority('medium');
    setNotes('');
    setSaving(false);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleFetch = async () => {
    const trimmed = cnr.trim();
    if (!trimmed) return;
    setFetching(true);
    setFetchError(null);
    setExistingId(null);
    try {
      const { data } = await casesAPI.fetchCnr(trimmed);
      setCaseData(data);
      setStep('preview');
    } catch (err) {
      const status = err.response?.status;
      const errData = err.response?.data;
      if (status === 409) {
        setFetchError('This case already exists.');
        setExistingId(errData?.existing_id || null);
      } else if (status === 400) {
        setFetchError('Invalid CNR format. Please check and try again.');
      } else if (status === 502) {
        setFetchError(errData?.error || 'eCourts API is unavailable. Please try again later.');
      } else {
        setFetchError('Failed to fetch case. Please try again.');
      }
    } finally {
      setFetching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFetch();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        cnr: caseData.cnr,
        priority,
      };
      if (assignedLawyer) payload.assigned_lawyer = assignedLawyer;
      if (notes.trim()) payload.internal_notes = notes.trim();

      const { data } = await casesAPI.create(payload);
      toast.success('Case added successfully!');
      resetAll();
      onClose();
      onSaved();
      navigate(`/cases/${data.id}`);
    } catch (err) {
      const errData = err.response?.data;
      toast.error(errData?.error || errData?.cnr?.[0] || 'Failed to save case. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const lawyerOptions = [
    { value: '', label: 'Unassigned' },
    ...lawyers.map(l => ({ value: String(l.id), label: l.full_name || l.username })),
  ];

  const filingDateFmt = safeFormat(caseData?.filing_date, 'dd MMM yyyy');
  const nextHearingFmt = safeFormat(caseData?.next_hearing_date, 'dd MMM yyyy');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-case-modal-title"
        className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 id="add-case-modal-title" className="text-lg font-bold text-gray-900">
            {step === 'input' ? 'Add Case via CNR' : 'Case Preview'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* ── Step 1: CNR Input ── */}
        {step === 'input' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CNR Number
              </label>
              <input
                ref={cnrInputRef}
                type="text"
                value={cnr}
                onChange={e => {
                  setCnr(e.target.value.toUpperCase());
                  setFetchError(null);
                  setExistingId(null);
                }}
                onKeyDown={handleKeyDown}
                placeholder="MHPU020002002023"
                disabled={fetching}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              />
              <p className="text-xs text-gray-400 mt-1">
                Enter the 16-character CNR number from eCourts.
              </p>

              {/* Inline error */}
              {fetchError && (
                <div className="mt-2">
                  <p className="text-sm text-red-500">{fetchError}</p>
                  {existingId && (
                    <button
                      type="button"
                      onClick={() => { navigate(`/cases/${existingId}`); handleClose(); }}
                      className="text-sm text-blue-600 underline hover:text-blue-800 mt-0.5"
                    >
                      View existing case
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFetch}
                disabled={fetching || !cnr.trim()}
                className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl transition-colors hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: '#1a2744' }}
              >
                {fetching ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Fetching...
                  </>
                ) : (
                  'Fetch from eCourts'
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Preview + Internal Fields ── */}
        {step === 'preview' && caseData && (
          <div className="space-y-5">
            {/* Read-only preview grid */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-2">
                Case Details from eCourts
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <PreviewField label="CNR" value={caseData.cnr} />
                <PreviewField label="Case Number" value={caseData.case_number} />
                <PreviewField label="Case Type" value={caseData.case_type_raw || caseData.case_type} />
                <PreviewField label="Status" value={caseData.case_status} />
                <PreviewField label="Court Name" value={caseData.court_name} />
                <PreviewField label="District" value={caseData.district} />
                <PreviewField label="State" value={caseData.state} />
                <PreviewField label="Stage" value={caseData.stage_of_case} />
                <PreviewField label="Filing Date" value={filingDateFmt} />
                <PreviewField label="Next Hearing" value={nextHearingFmt} />
                <PreviewField label="Purpose" value={caseData.purpose} />
                <PreviewField label="Hearing Count" value={caseData.hearing_count} />
                <PreviewField label="Petitioners" value={caseData.petitioners} colSpan={2} />
                <PreviewField label="Respondents" value={caseData.respondents} colSpan={2} />
                <PreviewField label="Judges" value={caseData.judges} colSpan={2} />
                <PreviewField label="Petitioner Advocates" value={caseData.petitioner_advocates} colSpan={2} />
                <PreviewField label="Respondent Advocates" value={caseData.respondent_advocates} colSpan={2} />
              </div>
            </div>

            {/* Internal fields */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Internal Details</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Assigned Lawyer</label>
                  <SearchableSelect
                    value={assignedLawyer}
                    onChange={setAssignedLawyer}
                    options={lawyerOptions}
                    placeholder="Unassigned"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <SearchableSelect
                    value={priority}
                    onChange={setPriority}
                    options={PRIORITY_SELECT_OPTIONS}
                    placeholder="Select priority"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Internal Notes</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Optional internal notes..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setStep('input')}
                disabled={saving}
                className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Back
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl transition-colors hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: '#1a2744' }}
              >
                {saving ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Case'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Case Card ───────────────────────────────────────── */
function CaseCard({ c, onClick, onSyncDone }) {
  const p = PRIORITY[c.priority?.toLowerCase()] || PRIORITY.medium;
  const [syncing, setSyncing] = useState(false);

  const caseTypePretty = c.case_type_raw || c.case_type || null;

  const handleSync = async (e) => {
    e.stopPropagation();
    if (syncing) return;
    setSyncing(true);
    try {
      await casesAPI.syncCase(c.id);
      toast.success('Case synced successfully!');
      if (onSyncDone) onSyncDone();
    } catch {
      toast.error('Failed to sync case. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const nextDateFmt = safeFormat(c.next_hearing_date, 'dd MMM yyyy');
  const syncedFmt = c.last_synced_at
    ? safeFormat(c.last_synced_at, 'dd MMM HH:mm')
    : null;

  const lawyerDetail = c.assigned_lawyer_detail;
  const lawyerName = lawyerDetail
    ? (lawyerDetail.full_name || lawyerDetail.username)
    : null;

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${p.border} cursor-pointer hover:shadow-md transition-all p-4 space-y-3`}
    >
      {/* Title + Priority badge + Sync button */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-gray-900 text-sm leading-snug flex-1 line-clamp-2">
          {c.petitioners
            ? <>{c.petitioners} <span className="text-gray-400 font-normal">vs.</span> {c.respondents}</>
            : c.case_number
          }
        </h3>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${p.badge}`}>
            {p.label}
          </span>
          <button
            onClick={handleSync}
            disabled={syncing}
            title="Sync from eCourts"
            aria-label="Sync case from eCourts"
            className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tags: status + case type */}
      <div className="flex items-center gap-2 flex-wrap">
        {c.case_status && (
          <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full capitalize font-medium">
            {c.case_status}
          </span>
        )}
        {caseTypePretty && (
          <span className="text-xs text-gray-400">{caseTypePretty}</span>
        )}
      </div>

      {/* CNR + Case number */}
      <div className="flex flex-col gap-0.5 text-xs text-gray-500">
        {c.cnr && (
          <span className="flex items-center gap-1">
            <Hash size={11} className="text-gray-400 shrink-0" />
            <span className="font-mono">{c.cnr}</span>
          </span>
        )}
        {c.case_number && (
          <span className="flex items-center gap-1">
            <Hash size={11} className="text-gray-400 shrink-0" />
            {c.case_number}
          </span>
        )}
        {(c.court_name || c.district) && (
          <span className="flex items-center gap-1 truncate">
            <MapPin size={11} className="text-gray-400 shrink-0" />
            <span className="truncate">
              {[c.court_name, c.district].filter(Boolean).join(' · ')}
            </span>
          </span>
        )}
      </div>

      {/* Assigned lawyer */}
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <User size={12} className="text-gray-400 shrink-0" />
        {lawyerName
          ? <span>{lawyerName}</span>
          : <span className="text-gray-300">Unassigned</span>
        }
      </div>

      {/* Next hearing */}
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
        <Calendar size={12} className="text-gray-400" />
        {nextDateFmt
          ? <>Next: {nextDateFmt}</>
          : <span className="text-gray-300 font-normal">No hearing scheduled</span>
        }
      </div>

      {/* Last synced */}
      <div className="text-right text-xs text-gray-400">
        Synced: {syncedFmt || 'Never synced'}
      </div>
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────── */
export default function CasesListPage() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (search)         params.search      = search;
      if (statusFilter)   params.case_status = statusFilter;
      if (typeFilter)     params.case_type   = typeFilter;
      if (priorityFilter) params.priority    = priorityFilter;
      const { data } = await casesAPI.list(params);
      setCases(data.results || data);
      setCount(data.count || (data.results || data).length);
    } catch {
      toast.error('Failed to load cases. Please try again.');
      setCases([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchCases(); }, [search, statusFilter, typeFilter, priorityFilter, page]); // eslint-disable-line
  const resetPage = () => setPage(1);

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Case Management</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors hover:opacity-90"
          style={{ backgroundColor: '#1a2744' }}
        >
          <Plus size={16} /> Add Case
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search cases..."
          value={search}
          onChange={e => { setSearch(e.target.value); resetPage(); }}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="w-40">
          <SearchableSelect value={statusFilter} onChange={v => { setStatusFilter(v); resetPage(); }}
            options={STATUS_OPTIONS} placeholder="All Status" />
        </div>
        <div className="w-40">
          <SearchableSelect value={typeFilter} onChange={v => { setTypeFilter(v); resetPage(); }}
            options={TYPE_OPTIONS} placeholder="All Types" />
        </div>
        <div className="w-40">
          <SearchableSelect value={priorityFilter} onChange={v => { setPriorityFilter(v); resetPage(); }}
            options={PRIORITY_OPTIONS} placeholder="All Priority" />
        </div>
      </div>

      {/* Count */}
      <p className="text-sm font-semibold text-gray-800">Cases ({count})</p>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-20 text-gray-400 text-sm">Loading cases...</div>
      ) : cases.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <FolderOpen size={44} className="mx-auto mb-3 opacity-25" />
          <p className="text-sm">No cases found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cases.map(c => (
            <CaseCard
              key={c.id}
              c={c}
              onClick={() => navigate(`/cases/${c.id}`)}
              onSyncDone={fetchCases}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {count > 20 && (
        <div className="flex justify-center gap-2 pt-2">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
            className="px-4 py-2 text-sm border border-gray-200 rounded-xl bg-white disabled:opacity-40 hover:bg-gray-50">
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-500 bg-white border border-gray-200 rounded-xl">Page {page}</span>
          <button disabled={page * 20 >= count} onClick={() => setPage(p => p + 1)}
            className="px-4 py-2 text-sm border border-gray-200 rounded-xl bg-white disabled:opacity-40 hover:bg-gray-50">
            Next
          </button>
        </div>
      )}

      {/* Add Case Modal */}
      {showAddModal && (
        <AddCaseModal
          onClose={() => setShowAddModal(false)}
          onSaved={fetchCases}
        />
      )}
    </div>
  );
}
